import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { logClientError } from '@/lib/errorLogger'

interface Props {
  children: ReactNode
  /** Página o sección que envuelve, solo para diagnóstico (ver `logClientError`). */
  page: string
  /** Se llama además de limpiar `hasError` -- el padre (`PageBoundary`) lo usa para forzar
   * un remount limpio del árbol (vía `key`), que a su vez hace que TanStack Query vuelva a
   * ejecutar cualquier consulta que hubiera quedado en error. */
  onRetry?: () => void
  /** Si se da, se muestra el botón "Volver al inicio". */
  onGoHome?: () => void
}

interface State {
  hasError: boolean
}

/** Red de seguridad de última instancia -- sin esto, cualquier excepción de render en
 * cualquier pantalla deja la Mini App completamente en blanco (pasó con Bankroll: una moneda
 * no-ISO en datos antiguos rompía `Intl.NumberFormat`). Nunca debería activarse si cada
 * pantalla maneja bien sus propios estados de carga/error, pero si algo se escapa, esto
 * muestra un mensaje recuperable en vez de una pantalla vacía. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logClientError({
      page: this.props.page,
      component: info.componentStack?.trim().split('\n')[0]?.trim(),
      errorType: error.name || 'render_error',
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false })
    this.props.onRetry?.()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center gap-3">
          <span className="h-16 w-16 rounded-2xl bg-warn-soft flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-warn" />
          </span>
          <h1 className="text-base font-semibold">Ocurrió un problema</h1>
          <p className="text-sm text-text-muted max-w-xs">No pudimos cargar esta información.</p>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium"
            >
              Reintentar
            </button>
            {this.props.onGoHome && (
              <button
                onClick={this.props.onGoHome}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-text"
              >
                Volver al inicio
              </button>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
